import * as React from "react"
import { type ReadInvitationRecipientQuery } from "@digits-graphql/frontend/graphql-public"
import useRedirectToApp from "@digits-shared/hooks/useRedirectToApp"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import { useReadInvitationRecipient } from "src/frontend/components/Public/hooks/useReadInvitationRecipient"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"

export function useLocationRedirect() {
  const { location } = useRouter()
  const session = useSession<FrontendSession>()
  const sessionRootUrl = getRootUrl(session, location.name)
  const { recipientData } = useReadInvitationRecipient()
  const redirectToApp = useRedirectToApp(sessionRootUrl)
  const redirectToOnboardPartner = useRedirectToApp(
    routes.welcome.generateFromCurrentPath({}, "includeAllQueryParams"),
    { partner: true }
  )

  const recipientIsExistingUser = (data?: ReadInvitationRecipientQuery) =>
    !!(data && data.readInvitationRecipient.userId)

  // If signed-in user came from the /onboard/partner?t=<token> path, redirect there
  const redirect = recipientIsExistingUser(recipientData) ? redirectToOnboardPartner : redirectToApp

  return React.useMemo(
    () => ({
      redirect,
      sessionRootUrl,
    }),
    [redirect, sessionRootUrl]
  )
}

function getRootUrl(
  { currentOrganization, currentLegalEntity }: FrontendSession,
  locationName: string
) {
  // If we are currently in the collect funds flow, include the `c` token and remove the `ml` token if present.
  if (locationName === routes.collectFunds.name) {
    return routes.collectFunds.generateFromCurrentPath({}, "includeAllQueryParams", ["ml"])
  }

  if (currentOrganization?.hasAffiliations) {
    return routes.root.parameterizedPath
  }

  if (currentLegalEntity?.slug) {
    return routes.legalEntityHome.generate({ leSlug: currentLegalEntity?.slug })
  }

  return routes.root.parameterizedPath
}
